import { type ReactElement } from 'react';
import { Link, redirect, useLoaderData } from 'react-router';
import type { LoaderFunctionArgs } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Badge, EmptyState } from '@marketplace/ui';
import { type Job } from '@marketplace/contracts';
import { localeOf } from '../features/auth/actions.js';
import { queryKeys, routeContext } from '../shared/query.js';
import { loadSession } from '../shared/session.js';
import { jobTone } from '../shared/status-tone.js';

/**
 * The client's own jobs — `W4-T04` §3.2.
 *
 * **This is `W4`'s page, not `W12`'s, and the precedent is `W2`'s.** ADR-011 gives `agent-ui` the
 * *storefront*: the public, indexable pages a visitor reaches without an account. An authenticated
 * area over a slice's own data belongs to the slice that owns the data, which is what `W2-T09`
 * did with `/account`. This is the list a client reaches their quotes through.
 *
 * **Posting starts here** since `W4-T10`, which built the form `W4-T01` left as its tail. The link is
 * rendered with jobs and without, because the empty state is exactly when somebody needs it.
 */
interface JobsData {
  locale: string;
  jobs: Job[];
}

export async function loader({ params, context }: LoaderFunctionArgs): Promise<JobsData> {
  const locale = localeOf(params);

  // The same guard `/account` uses, and for the same reason: this page has no content without a
  // user, so an empty shell with "you are not signed in" would be a worse login page than the one
  // that exists.
  const session = await loadSession(context);
  if (session === null) throw redirect(`/${locale}/login`);

  const { queryClient, api } = context.get(routeContext);
  const jobs = await queryClient.ensureQueryData({
    queryKey: queryKeys.myJobs(),
    queryFn: () => api.getMyJobs(),
  });

  return { locale, jobs };
}

export function Component(): ReactElement {
  const { t } = useTranslation();
  const { locale, jobs } = useLoaderData<JobsData>();

  const post = (
    <p>
      <Link to={`/${locale}/jobs/new`}>{t('jobs.post')}</Link>
    </p>
  );

  if (jobs.length === 0) {
    return (
      <article data-testid="jobs">
        <h1>{t('jobs.title')}</h1>
        <EmptyState title={t('jobs.empty.title')} description={t('jobs.empty.body')} />
        {post}
      </article>
    );
  }

  return (
    <article data-testid="jobs">
      <h1>{t('jobs.title')}</h1>
      {post}
      <ul className="mp-list">
        {jobs.map((job) => (
          <li key={job.id} className="mp-section">
            <h2>
              <Link to={`/${locale}/jobs/${job.id}`}>{job.title ?? t('jobs.untitled')}</Link>
            </h2>
            {/* The status is a translated word, never the enum: `OPEN` is not Spanish. */}
            <p className="mp-meta">
              <Badge tone={jobTone(job.status)}>{t(`jobs.status.${job.status}`)}</Badge>
            </p>
          </li>
        ))}
      </ul>
    </article>
  );
}
